/**
 * MBR (DOS Master Boot Record) parser.
 *
 * Reads LBA 0 (512 bytes), pulls 4 partition entries from offset 446,
 * decodes type bytes against the standard map.
 *
 * Surface: window.__DiskImage.parseMbr(reader) → Promise<{
 *   bootloader: Uint8Array(440),
 *   diskSignature: number,
 *   partitions: Partition[],
 *   isProtective: bool,    // 0xEE present
 *   isHybrid: bool,        // protective MBR + non-EE entries
 *   signatureOk: bool      // 0x55AA at offset 510
 * }>
 *
 * Partition: {
 *   index, bootable, type, typeName, startLba, sizeLba,
 *   chsStart: {h, s, c}, chsEnd: {h, s, c}
 * }
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.parseMbr) return;

    var TYPE_MAP = {
        0x00: 'Empty',
        0x01: 'FAT12',
        0x04: 'FAT16 (<32M)',
        0x05: 'Extended (CHS)',
        0x06: 'FAT16',
        0x07: 'NTFS / exFAT / HPFS',
        0x0B: 'FAT32 (CHS)',
        0x0C: 'FAT32 (LBA)',
        0x0E: 'FAT16 (LBA)',
        0x0F: 'Extended (LBA)',
        0x11: 'Hidden FAT12',
        0x14: 'Hidden FAT16 (<32M)',
        0x16: 'Hidden FAT16',
        0x17: 'Hidden NTFS',
        0x1B: 'Hidden FAT32 (CHS)',
        0x1C: 'Hidden FAT32 (LBA)',
        0x27: 'Recovery (NTFS)',
        0x42: 'Dynamic Disk',
        0x82: 'Linux swap / Solaris',
        0x83: 'Linux',
        0x85: 'Linux extended',
        0x86: 'NTFS volume set',
        0x8E: 'Linux LVM',
        0xA5: 'FreeBSD',
        0xA6: 'OpenBSD',
        0xA8: 'Mac OS X UFS',
        0xA9: 'NetBSD',
        0xAB: 'Mac OS X boot',
        0xAF: 'Mac OS X HFS',
        0xBE: 'Solaris boot',
        0xBF: 'Solaris',
        0xDA: 'Non-FS data',
        0xDE: 'Dell Utility',
        0xEE: 'GPT Protective',
        0xEF: 'EFI System (FAT)',
        0xFB: 'VMware VMFS',
        0xFD: 'Linux RAID auto'
    };

    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }

    function _decodeChs(b, off) {
        var h = b[off];
        var s = b[off + 1] & 0x3F;
        var cHi = (b[off + 1] & 0xC0) << 2;
        var cLo = b[off + 2];
        return { h: h, s: s, c: cHi | cLo };
    }

    function parseMbr(reader) {
        return reader.bytes(0, 512).then(function (b) {
            var signatureOk = b[510] === 0x55 && b[511] === 0xAA;
            var diskSig = _u32le(b, 440);
            var bootloader = b.slice(0, 440);
            var partitions = [];
            var protective = false;
            for (var i = 0; i < 4; i++) {
                var off = 446 + i * 16;
                var bootable = b[off] === 0x80;
                var type = b[off + 4];
                var startLba = _u32le(b, off + 8);
                var sizeLba = _u32le(b, off + 12);
                if (type === 0xEE) protective = true;
                if (type === 0 && startLba === 0 && sizeLba === 0) continue;
                partitions.push({
                    index: i,
                    bootable: bootable,
                    type: type,
                    typeName: TYPE_MAP[type] || 'Unknown (0x' + type.toString(16) + ')',
                    startLba: startLba,
                    sizeLba: sizeLba,
                    sizeBytes: sizeLba * 512,
                    chsStart: _decodeChs(b, off + 1),
                    chsEnd: _decodeChs(b, off + 5)
                });
            }
            var isHybrid = protective && partitions.some(function (p) { return p.type !== 0xEE && p.type !== 0; });
            return {
                bootloader: bootloader,
                diskSignature: diskSig,
                partitions: partitions,
                isProtective: protective,
                isHybrid: isHybrid,
                signatureOk: signatureOk
            };
        });
    }

    w.__DiskImage.parseMbr = parseMbr;
    w.__DiskImage.MBR_TYPES = TYPE_MAP;
})(window);
